import { ReactNode, useEffect, useState, useContext } from 'react';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import isString from 'lodash/isString';
import styled, { ThemeProvider, css, ThemeContext } from 'styled-components';

import { Backdrop } from 'shared/components/Backdrop';
import { ClientOnlyPortal } from 'shared/components/ClientOnlyPortal';
import { useOutsideClick } from 'shared/hooks/useOutsideClick';
import { useKeyDown } from 'shared/hooks/useKeyDown';
import { variableFontMedium, variableFontRegular } from 'styles/theme/text';
import { t } from 'localization/utils/t';
import { navigationThemes } from 'styles/theme/navigation';

const TP = 'shared.components.Modal';

export interface IModalProps {
  'data-qa'?: string;
  children: ReactNode;
  className?: string;
  disableOutsideClick?: boolean;
  dataQa?: string;
  darkTheme?: boolean;
  lightTheme?: boolean;
  modalCloseText?: string;
  onClose: () => void;
  onScroll?: (e) => void;
  title: string | ReactNode;
}

export const Modal = ({
  children,
  className,
  'data-qa': dataQa,
  disableOutsideClick,
  darkTheme,
  lightTheme,
  modalCloseText,
  onClose,
  onScroll,
  title,
}: IModalProps) => {
  const themeContext = useContext(ThemeContext);
  const modalRef = useOutsideClick(onClose, disableOutsideClick);
  const [modalTheme, setModalTheme] = useState(themeContext);
  const closeModalText = modalCloseText || t(`${TP}.close`, 'Close');

  useEffect(() => {
    // Note: in order to get the ref on the next render
    if (modalRef.current) {
      setTimeout(() => disableBodyScroll(modalRef.current));
    }

    return () => clearAllBodyScrollLocks();
  }, []);

  useEffect(() => {
    if (darkTheme) {
      setModalTheme({
        ...themeContext,
        navigation: navigationThemes.dark,
      });
      return;
    }
    if (lightTheme) {
      setModalTheme({
        ...themeContext,
        navigation: navigationThemes.light,
      });
      return;
    }
    // Else set to default theme for page.
    setModalTheme(themeContext);
  }, [darkTheme, lightTheme, themeContext]);

  useKeyDown('Escape', onClose);

  return (
    <ClientOnlyPortal selector="#main-page-layout">
      <Backdrop>
        <ThemeProvider theme={{ ...modalTheme }}>
          <Modal.Modal
            className={className}
            data-qa={dataQa}
            $darkTheme={darkTheme}
            onScroll={onScroll}
            ref={modalRef}
          >
            <Modal.Header>
              {isString(title) ? <Modal.Title data-qa="modal_title">{title}</Modal.Title> : title}
              <Modal.Close data-qa="modal_close_button" onClick={onClose}>
                {closeModalText}
              </Modal.Close>
            </Modal.Header>
            <Modal.Body>{children}</Modal.Body>
          </Modal.Modal>
        </ThemeProvider>
      </Backdrop>
    </ClientOnlyPortal>
  );
};

Modal.Modal = styled.div<{ $darkTheme: IModalProps['darkTheme'] }>`
  display: flex;
  flex-direction: column;
  min-width: 335px;
  position: relative;
  z-index: ${({ theme }) => theme.zIndex.modal};
  ${({ theme }) => {
    return css`
      background-color: ${theme.navigation.backgroundColor};
      color: ${theme.navigation.textColor};
      border: ${theme.navigation.border};

      input {
        border-bottom: ${theme.navigation.border};
      }
    `;
  }}
`;

Modal.Header = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  padding: 16px 16px 0;
`;

Modal.Body = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 16px 24px;
`;

Modal.Title = styled.h1`
  margin-block-end: 0em;
  margin-block-start: 0em;
  max-width: 225px;
  ${variableFontMedium({ size: '11.5px', height: '14px' })};
`;

Modal.Close = styled.button`
  cursor: pointer;
  margin-left: 50px;
  ${variableFontRegular({ size: '9.5px', height: '9px' })};

  &:hover {
    text-decoration: underline;
  }
`;
